import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiPlus, FiMinus, FiEye, FiEyeOff } from "react-icons/fi";
import api from "../../../../utils/apiConfig";
import PhoneInput from "../../../shared/PhoneInput";
import NameInput from "../../../shared/NameInput";
import MemberCard from "./household-information/MemberCard";
import FamilyMemberCard from "./household-information/FamilyMemberCard";
import { useAnnualReview } from "../../../../hooks/useAnnualReview";
import axios from "axios";
import OverviewCard from "./household-information/OverviewCard";

import logger from '../../../../utils/logger';

const inputClass =
  "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-900 dark:text-gray-100";
const selectClass =
  "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default";

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7)
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const formatSSN = (value) => {
  if (!value) return value;
  const ssn = value.replace(/[^\d]/g, "");
  if (ssn.length < 4) return ssn;
  if (ssn.length < 6) return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
};

const formatZipCode = (value) => {
  if (!value) return value;
  const zip = value.replace(/[^\d]/g, "");
  if (zip.length < 6) return zip;
  return `${zip.slice(0, 5)}-${zip.slice(5, 9)}`;
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const validatePhoneNumber = (phone) => {
  const stripped = phone.replace(/[^\d]/g, "");
  return stripped.length === 10;
};

const validateSSN = (ssn) => {
  if (!ssn) return true;
  const stripped = ssn.replace(/[^\d]/g, "");
  return stripped.length === 9;
};

const ValidationMessage = ({ show, message }) => {
  if (!show) return null;
  return <p className="mt-1 text-sm text-red-600">{message}</p>;
};

// API configuration is now centralized in apiConfig.js

const HouseholdInformation = () => {
  const navigate = useNavigate();
  const { householdId, userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [permissionLevel, setPermissionLevel] = useState(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const [showSpouseSSN, setShowSpouseSSN] = useState(false);
  const [formData, setFormData] = useState({
    personalInfo: {
      prefix: "",
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      gender: "",
      dateOfBirth: "",
      maritalStatus: "",
      email: "",
      phoneNumber: "",
      phoneType: "",
      alternatePhone: "",
      alternatePhoneType: "",
      ssn: "",
    },
    spouseInfo: {
      prefix: "",
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      preferredName: "",
      dateOfBirth: "",
      ssn: "",
      gender: "",
      email: "",
      phoneNumber: "",
      sharedEmail: false,
      phoneType: null,
      alternatePhone: "",
      alternatePhoneType: null,
      dateOfDeath: "",
      isTrustedContact: false,
    },
    address: {
      street: "",
      unit: "",
      city: "",
      state: "",
      zipCode: "",
      isMailingDifferent: false,
      mailingAddress: {
        street: "",
        unit: "",
        city: "",
        state: "",
        zipCode: "",
      },
    },
    familyMembers: [],
    taxInfo: { filingStatus: "", filingState: "" },
    marriageInfo: {
      marriageDate: "",
      divorceDate: "",
      cityOfMarriage: "",
      stateOfMarriage: "",
    },
  });
  const [errors, setErrors] = useState({
    personalInfo: {
      ssn: false,
      alternatePhone: false,
      email: false,
      phoneNumber: false,
    },
    spouseInfo: { ssn: false, email: false, phoneNumber: false },
    familyMembers: [],
  });
  const [referenceData, setReferenceData] = useState({
    prefixes: [],
    suffixes: [],
    genderTypes: [],
    maritalStatusTypes: [],
  });
  const [isSpouseSection, setIsSpouseSection] = useState(false);
  const [trustedContactCount, setTrustedContactCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [maritalStatusTypes, setMaritalStatusTypes] = useState([]);
  const [taxFilingStatusOptions, setTaxFilingStatusOptions] = useState([]);
  const [filingStateOptions, setFilingStateOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [client, setClient] = useState(null);
  const [familyCount, setFamilyCount] = useState({ total: 0, dependents: 0 });
  const [pipelineData, setPipelineData] = useState({
    stages: [],
    currentStage: null,
  });

  // Use the annual review hook
  const { annualReviewData, loading: annualReviewLoading } =
    useAnnualReview(householdId);

  const genderOptions = useMemo(
    () => [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
      { value: "prefer_not_to_say", label: "Prefer Not to Say" },
    ],
    [],
  );

  const relationshipTypes = useMemo(
    () => [
      { value: "child", label: "Child" },
      { value: "parent", label: "Parent" },
      { value: "sibling", label: "Sibling" },
      { value: "grandparent", label: "Grandparent" },
      { value: "grandchild", label: "Grandchild" },
      { value: "aunt_uncle", label: "Aunt/Uncle" },
      { value: "niece_nephew", label: "Niece/Nephew" },
      { value: "cousin", label: "Cousin" },
      { value: "other", label: "Other" },
    ],
    [],
  );

  const prefixOptions = useMemo(
    () => [
      { value: "Mr.", label: "Mr." },
      { value: "Mrs.", label: "Mrs." },
      { value: "Ms.", label: "Ms." },
      { value: "Dr.", label: "Dr." },
    ],
    [],
  );

  const suffixOptions = useMemo(
    () => [
      { value: "Jr.", label: "Jr." },
      { value: "Sr.", label: "Sr." },
      { value: "II", label: "II" },
      { value: "III", label: "III" },
      { value: "IV", label: "IV" },
      { value: "V", label: "V" },
      { value: "PhD", label: "PhD" },
      { value: "MD", label: "MD" },
    ],
    [],
  );

  const getMaritalStatusConfig = useCallback(
    (statusId) => {
      const id = Number(statusId);
      const status = maritalStatusTypes.find((s) => s.marital_status_id === id);
      const statusName = status?.marital_status_name || "";
      const statusLower = statusName.toLowerCase();
      return {
        input: id,
        resolvedName: statusName,
        statusLower: statusLower,
        showSpouse: ["married", "separated", "widowed"].includes(statusLower),
        showMarriage: ["married", "separated", "divorced", "widowed"].includes(
          statusLower,
        ),
        showDivorceDate: ["divorced", "separated"].includes(statusLower),
        showDeathDate: ["widowed"].includes(statusLower),
      };
    },
    [maritalStatusTypes],
  );

  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);

      let taxInfoResponseData = null;

      try {
        // First get the tax info from the database
        const dbTaxInfoResponse = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/tax-info`
        );
        taxInfoResponseData = dbTaxInfoResponse.data;
        
        if (taxInfoResponseData) {
          setTaxFilingStatusOptions(taxInfoResponseData.taxFilingStatuses || []);
          setFilingStateOptions(taxInfoResponseData.filingStates || []);
          setStateOptions(
            (taxInfoResponseData.filingStates || []).map((state) => ({
              state_id: state.id || state.state_id,
              state_code: state.code || state.state_code,
              state_name: state.name || state.state_name,
            })),
          );

          // Set default values from our database query
          const defaultFilingStatus = "Married Filing Jointly";
          const defaultState = "MI";

          // Find matching filing status ID from the API response
          const matchingStatus = taxInfoResponseData.taxFilingStatuses?.find(
            (status) => status.name === defaultFilingStatus
          );

          // Find matching state ID from the API response
          const matchingState = taxInfoResponseData.filingStates?.find(
            (state) => state.code === defaultState
          );

          // Update form state with tax info
          setFormData(prev => ({
            ...prev,
            taxInfo: {
              filingStatus: matchingStatus?.id?.toString() || "",
              filingState: matchingState?.id?.toString() || "",
            }
          }));
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          logger.error("Tax info fetch error:", error.message);
        }
      }

      logger.info("🔍 Fetching client data for:", { householdId, userId });
      const response = await api.get(
        `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/household-information`,
      );

      if (response.data) {
        const clientData = response.data;
        logger.info("Client data received:", clientData);

        // Log specific sections for debugging
        logger.info("Contact info:", clientData.ContactInformation);
        logger.info("Name info:", clientData.Name);
        logger.info(
          "Marital info:",
          clientData.maritalInfo || clientData.MaritalInformation,
        );
        logger.info("Marital status:", clientData.marital_status_id);

        // Set isSpouseSection based on marital status
        const maritalStatusId = clientData.marital_status_id?.toString() || "";
        const config = getMaritalStatusConfig(maritalStatusId);
        setIsSpouseSection(config.showSpouse);
        logger.info("Setting spouse section:", {
          maritalStatusId,
          showSpouse: config.showSpouse,
          resolvedName: config.resolvedName,
        });

        // Update form data with client info
        setFormData((prev) => {
          const newData = {
            ...prev,
            personalInfo: {
              ...prev.personalInfo,
              prefix: clientData.Name?.prefix || "",
              firstName: clientData.Name?.first_name || "",
              middleName: clientData.Name?.middle_name || "",
              lastName: clientData.Name?.last_name || "",
              suffix: clientData.Name?.suffix || "",
              preferredName: clientData.Name?.preferred_name || "",
              dateOfBirth: clientData.date_of_birth || "",
              ssn: clientData.ssn || "",
              gender: clientData.gender_type_id?.toString() || "",
              maritalStatus: maritalStatusId,
              email: clientData.ContactInformation?.email || "",
              phoneNumber: clientData.ContactInformation?.primary_number || "",
              phoneType:
                clientData.ContactInformation?.primary_number_type || null,
              alternatePhone:
                clientData.ContactInformation?.secondary_number || "",
              alternatePhoneType:
                clientData.ContactInformation?.secondary_number_type || null,
            },
          };

          // Handle marriage info from either source
          const marriageInfo =
            clientData.maritalInfo || clientData.MaritalInformation;
          if (marriageInfo) {
            newData.marriageInfo = {
              marriageDate:
                marriageInfo.marriage_date || marriageInfo.marriageDate || "",
              cityOfMarriage:
                marriageInfo.city_of_marriage ||
                marriageInfo.cityOfMarriage ||
                "",
              stateOfMarriage:
                marriageInfo.FilingState?.state_code ||
                (typeof marriageInfo.stateOfMarriage === "object"
                  ? marriageInfo.stateOfMarriage?.code
                  : marriageInfo.stateOfMarriage) ||
                "",
              divorceDate:
                marriageInfo.divorce_date || marriageInfo.divorceDate || "",
            };
          }

          // Set tax info with default values
          const defaultFilingStatus = "Married Filing Jointly";
          const defaultState = "MI";

          if (taxInfoResponseData) {
            const filingStatusMatch = taxInfoResponseData.taxFilingStatuses.find(
              (status) => status.name === defaultFilingStatus
            );
            const filingStateMatch = taxInfoResponseData.filingStates.find(
              (state) => state.code === defaultState
            );

            newData.taxInfo = {
              filingStatus: filingStatusMatch?.id?.toString() || "",
              filingState: filingStateMatch?.id?.toString() || "",
            };
          }

          return newData;
        });

        setClient(clientData);

        // Fetch addresses
        logger.info("Fetching addresses for household:", householdId);
        try {
          // Get primary address
          const primaryResponse = await api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/address-by-type?type=1`,
          );
          const primaryAddress = primaryResponse.data;
          logger.info("Primary address found:", primaryAddress);

          // Get mailing address
          let mailingAddress = null;
          try {
            const mailingResponse = await api.get(
              `/advisor-portal/client-database/household-profile/${householdId}/address-by-type?type=5`,
            );
            mailingAddress = mailingResponse.data;
            logger.info("Mailing address found:", mailingAddress);
          } catch (err) {
            if (err.response?.status !== 404) {
              logger.error("Error fetching mailing address:", err);
            }
          }

          // Update form data with addresses
          setFormData((prev) => ({
            ...prev,
            address: {
              street: primaryAddress?.address_line1 || "",
              unit: primaryAddress?.address_line2 || "",
              city: primaryAddress?.city || "",
              state: primaryAddress?.state || "",
              zipCode: primaryAddress?.zip || "",
              mailingAddress: {
                street: mailingAddress?.address_line1 || "",
                unit: mailingAddress?.address_line2 || "",
                city: mailingAddress?.city || "",
                state: mailingAddress?.state || "",
                zipCode: mailingAddress?.zip || "",
              },
            },
          }));
        } catch (error) {
          logger.error("Error fetching addresses:", error);
        }

        // Fetch spouse data if exists
        if (clientData.spouse_id) {
          try {
            const spouseResponse = await api.get(
              `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${clientData.spouse_id}/household-information`,
            );
            if (spouseResponse.data) {
              const spouseData = spouseResponse.data;
              setFormData((prev) => ({
                ...prev,
                spouseInfo: {
                  prefix: spouseData.Name?.prefix || "",
                  firstName: spouseData.Name?.first_name || "",
                  middleName: spouseData.Name?.middle_name || "",
                  lastName: spouseData.Name?.last_name || "",
                  suffix: spouseData.Name?.suffix || "",
                  preferredName: spouseData.Name?.preferred_name || "",
                  dateOfBirth: spouseData.date_of_birth || "",
                  ssn: spouseData.ssn || "",
                  gender: spouseData.gender_type_id?.toString() || "",
                  email: spouseData.ContactInformation?.email || "",
                  phoneNumber:
                    spouseData.ContactInformation?.primary_number || "",
                  sharedEmail: false,
                  phoneType:
                    spouseData.ContactInformation?.primary_number_type || null,
                  alternatePhone:
                    spouseData.ContactInformation?.secondary_number || "",
                  alternatePhoneType:
                    spouseData.ContactInformation?.secondary_number_type ||
                    null,
                  dateOfDeath: "",
                  isTrustedContact: false,
                },
              }));
            }
          } catch (error) {
            logger.error("Failed to fetch spouse data:", error);
          }
        }
      }
    } catch (error) {
      logger.error("❌ Error fetching client data:", error);
      setError("Failed to fetch client data");
    } finally {
      setLoading(false);
    }
  }, [householdId, userId, annualReviewData, getMaritalStatusConfig]);

  const handleMaritalStatusChange = useCallback(
    (e) => {
      const newStatusId = e.target.value;
      const config = getMaritalStatusConfig(newStatusId);
      setIsSpouseSection(config.showSpouse);
      handleInputChange("personalInfo", "maritalStatus", "", newStatusId);
    },
    [getMaritalStatusConfig],
  );

  useEffect(() => {
    const initializeMaritalStatus = () => {
      if (
        formData.personalInfo.maritalStatus &&
        maritalStatusTypes.length > 0
      ) {
        const statusId = formData.personalInfo.maritalStatus;
        const config = getMaritalStatusConfig(statusId);
        setIsSpouseSection(config.showSpouse);
      }
    };
    initializeMaritalStatus();
  }, [
    formData.personalInfo.maritalStatus,
    maritalStatusTypes,
    getMaritalStatusConfig,
  ]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const fetchReferenceData = async () => {
    try {
      const [prefixesRes, suffixesRes, genderTypesRes, maritalStatusRes, taxInfoRes] =
        await Promise.all([
          api.get(
            "/advisor-portal/client-database/household-profile/reference-data/name-prefixes",
          ),
          api.get(
            "/advisor-portal/client-database/household-profile/reference-data/name-suffixes",
          ),
          api.get(
            "/advisor-portal/client-database/household-profile/reference-data/gender-types",
          ),
          api.get(
            "/advisor-portal/client-database/household-profile/reference-data/marital-status-types",
          ),
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/tax-info`,
          ),
        ]);

      setReferenceData({
        prefixes: prefixesRes.data,
        suffixes: suffixesRes.data,
        genderTypes: genderTypesRes.data,
        maritalStatusTypes: maritalStatusRes.data,
      });

      // Set tax filing status options
      if (taxInfoRes.data) {
        setTaxFilingStatusOptions(taxInfoRes.data.taxFilingStatuses || []);
        setFilingStateOptions(taxInfoRes.data.filingStates || []);
        setStateOptions(
          (taxInfoRes.data.filingStates || []).map((state) => ({
            state_id: state.id || state.state_id,
            state_code: state.code || state.state_code,
            state_name: state.name || state.state_name,
          })),
        );

        // Set default values from our database query
        const defaultFilingStatus = "Married Filing Jointly";
        const defaultState = "MI";

        // Find matching filing status ID from the API response
        const matchingStatus = taxInfoRes.data.taxFilingStatuses?.find(
          (status) => status.name === defaultFilingStatus
        );

        // Find matching state ID from the API response
        const matchingState = taxInfoRes.data.filingStates?.find(
          (state) => state.code === defaultState
        );

        // Update form state with tax info
        setFormData(prev => ({
          ...prev,
          taxInfo: {
            filingStatus: matchingStatus?.id?.toString() || "",
            filingState: matchingState?.id?.toString() || "",
          }
        }));
      }
    } catch (error) {
      logger.error("Error fetching reference data:", error);
      setError("Failed to load reference data");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchReferenceData(),
          // ... other initialization calls
        ]);
      } catch (error) {
        logger.error("Error initializing data:", error);
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    try {
      // Before sending the request, log the data in detail
      logger.info("🚨 SUBMITTING FORM DATA:", {
        maritalStatus: formData.personalInfo.maritalStatus,
        marriageInfo: formData.marriageInfo,
        marriageInfoDetails: {
          marriageDate: formData.marriageInfo.marriageDate,
          cityOfMarriage: formData.marriageInfo.cityOfMarriage,
          stateOfMarriage: formData.marriageInfo.stateOfMarriage,
          divorceDate: formData.marriageInfo.divorceDate,
        },
        address: formData.address,
        mailingAddress: formData.address.mailingAddress,
      });

      // Update address data format for the API
      const addressData = {
        address: {
          street: formData.address.street,
          unit: formData.address.unit,
          city: formData.address.city,
          state: formData.address.state,
          zipCode: formData.address.zipCode,
        },
        mailingAddress: formData.address.mailingAddress,
      };

      // Save address data
      await api.put(
        `/advisor-portal/client-database/household-profile/${householdId}/address`,
        addressData,
      );

      // Save tax info
      await api.put(
        `/advisor-portal/client-database/household-profile/${householdId}/tax-info`,
        {
          tax_filing_status_id: formData.taxInfo.filingStatus,
          filing_state_id: formData.taxInfo.filingState,
        }
      );

      const response = await api.put(
        `/api/v1/advisor-portal/client-database/${householdId}/client-profile/${userId}`,
        {
          personalInfo: formData.personalInfo,
          marriageInfo: formData.marriageInfo,
        },
      );

      logger.info("✅ Form submission response:", response.data);
      setSuccessMessage("Client information updated successfully");
    } catch (error) {
      logger.error("❌ Error submitting form:", error);
      setError("Failed to update client information");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, subfield, value) => {
    let formattedValue = value;
    let isValid = true;

    if (field === "email") {
      if (value.trim() === "") {
        setErrors((prev) => ({
          ...prev,
          [section]: { ...prev[section], email: false },
        }));
      } else {
        isValid = validateEmail(value);
        setErrors((prev) => ({
          ...prev,
          [section]: { ...prev[section], email: !isValid },
        }));
      }
    } else if (
      field === "phoneNumber" ||
      field === "alternatePhone" ||
      field === "phone"
    ) {
      formattedValue = formatPhoneNumber(value);
      isValid = !formattedValue || validatePhoneNumber(formattedValue);
      setErrors((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: !isValid && formattedValue !== "",
        },
      }));
    } else if (field === "ssn") {
      formattedValue = formatSSN(value);
      isValid = !formattedValue || validateSSN(formattedValue);
      setErrors((prev) => ({
        ...prev,
        [section]: { ...prev[section], ssn: !isValid && formattedValue !== "" },
      }));
    } else if (field === "zipCode") {
      formattedValue = formatZipCode(value);
    }

    setFormData((prev) => ({
      ...prev,
      [section]: subfield
        ? {
            ...prev[section],
            [field]: { ...prev[section][field], [subfield]: formattedValue },
          }
        : { ...prev[section], [field]: formattedValue },
    }));
  };

  const updateDependentCount = (index, isDependent) => {
    setFamilyCount((prev) => ({
      ...prev,
      dependents: isDependent ? prev.dependents + 1 : prev.dependents - 1,
    }));
  };

  const addFamilyMember = () => {
    setFormData((prev) => ({
      ...prev,
      familyMembers: [
        ...prev.familyMembers,
        {
          prefix: "",
          firstName: "",
          middleName: "",
          lastName: "",
          suffix: "",
          preferredName: "",
          gender: "",
          relationship: "",
          dateOfBirth: "",
          ssn: "",
          dependentStatus: false,
          isTrustedContact: false,
          isHouseholdMember: true,
          email: "",
          phone: "",
          showSSN: false,
          address: { street: "", city: "", state: "", zipCode: "" },
        },
      ],
    }));
    setErrors((prev) => ({
      ...prev,
      familyMembers: [
        ...prev.familyMembers,
        { ssn: false, email: false, phone: false },
      ],
    }));
    setFamilyCount((prev) => ({ ...prev, total: prev.total + 1 }));
  };

  const removeFamilyMember = (index) => {
    const member = formData.familyMembers[index];
    setFormData((prev) => ({
      ...prev,
      familyMembers: prev.familyMembers.filter((_, i) => i !== index),
    }));
    setErrors((prev) => ({
      ...prev,
      familyMembers: prev.familyMembers.filter((_, i) => i !== index),
    }));
    setFamilyCount((prev) => ({
      total: prev.total - 1,
      dependents: member.dependentStatus
        ? prev.dependents - 1
        : prev.dependents,
    }));
  };

  const validateTrustedContactLimit = (newValue, currentValue) => {
    if (newValue && !currentValue && trustedContactCount >= 2) {
      setShowAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    let count = 0;
    if (formData.spouseInfo.isTrustedContact) count++;
    if (formData.familyMembers)
      count += formData.familyMembers.filter(
        (member) => member.isTrustedContact,
      ).length;
    setTrustedContactCount(count);
  }, [formData.familyMembers, formData.spouseInfo.isTrustedContact]);

  const renderPermissionBadge = () => {
    if (!permissionLevel) return null;
    const badgeColors = {
      admin: "bg-purple-100 text-purple-800 border-purple-200",
      advisor: "bg-blue-100 text-blue-800 border-blue-200",
      default: "bg-gray-100 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700",
    };
    const badgeColor = badgeColors[permissionLevel] || badgeColors.default;
    return (
      <div
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor} border`}
      >
        {permissionLevel.charAt(0).toUpperCase() + permissionLevel.slice(1)}
      </div>
    );
  };

  const handleFamilyMemberChange = (index, field, subfield, value) => {
    if (field === "email") {
      const isValid = !value || validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        familyMembers: prev.familyMembers.map((member, i) =>
          i === index ? { ...member, email: !isValid && value !== "" } : member,
        ),
      }));
    } else if (field === "phone") {
      value = formatPhoneNumber(value);
      const isValid = !value || validatePhoneNumber(value);
      setErrors((prev) => ({
        ...prev,
        familyMembers: prev.familyMembers.map((member, i) =>
          i === index ? { ...member, phone: !isValid && value !== "" } : member,
        ),
      }));
    } else if (field === "ssn") {
      value = formatSSN(value);
      const isValid = !value || validateSSN(value);
      setErrors((prev) => ({
        ...prev,
        familyMembers: prev.familyMembers.map((member, i) =>
          i === index ? { ...member, ssn: !isValid && value !== "" } : member,
        ),
      }));
    }

    setFormData((prev) => {
      const newFamilyMembers = [...prev.familyMembers];
      if (field.startsWith("address.")) {
        const addressField = field.split(".")[1];
        newFamilyMembers[index].address = {
          ...newFamilyMembers[index].address,
          [addressField]: value,
        };
      } else {
        newFamilyMembers[index] = {
          ...newFamilyMembers[index],
          [field]: value,
        };
      }
      return { ...prev, familyMembers: newFamilyMembers };
    });
  };

  const handleSharedEmailChange = (e) => {
    handleInputChange("spouseInfo", "sharedEmail", "", e.target.checked);
    if (e.target.checked) {
      handleInputChange("spouseInfo", "email", "", formData.personalInfo.email);
    } else {
      handleInputChange("spouseInfo", "email", "", "");
    }
  };

  // Process client data
  useEffect(() => {
    if (client) {
      logger.info("🚨 PROCESSING CLIENT DATA IN USE-EFFECT:", {
        clientObject: !!client,
        maritalStatus: client.maritalStatus?.marital_status_name,
        hasMaritalInfo: !!client.maritalInfo,
        maritalInfo: client.maritalInfo,
      });

      try {
        // Additional processing logic can be added here
      } catch (error) {
        logger.error("Error processing client data:", error);
        setError("Failed to process client data");
      }
    }
  }, [client]);

  // Add function to test direct SQL endpoint
  const testDirectSqlEndpoint = useCallback(async (hId, uId) => {
    if (!hId || !uId) return;

    try {
      logger.info("🧪 TESTING DIRECT SQL ENDPOINT:", {
        householdId: hId,
        userId: uId,
      });
      const response = await api.get(
        `/advisor-portal/client-database/household-profile/${hId}/client-profile/${uId}/household-information-direct`,
      );

      if (response.data) {
        logger.info("🧪 RAW SQL RESPONSE:", response.data);

        // Create marital info object from nested MaritalInformation
        const newMaritalInfo = {
          marriageDate: response.data.MaritalInformation?.marriage_date || "",
          cityOfMarriage:
            response.data.MaritalInformation?.city_of_marriage || "",
          stateOfMarriage:
            response.data.MaritalInformation?.FilingState?.state_code || "",
          divorceDate: response.data.MaritalInformation?.divorce_date || "",
        };

        logger.info("🧪 MAPPED MARITAL INFO:", newMaritalInfo);

        // Update client state with marital info
        setClient((prevClient) => {
          logger.info("🧪 PREVIOUS CLIENT STATE:", prevClient);
          const updatedClient = {
            ...prevClient,
            maritalInfo: newMaritalInfo,
            hasMarriageInfo: true,
            maritalStatus: {
              marital_status_id: response.data.marital_status_id,
              marital_status_name:
                response.data.maritalStatus?.marital_status_name,
            },
          };
          logger.info("🧪 UPDATED CLIENT STATE:", updatedClient);
          return updatedClient;
        });

        // Update form data with marital info
        setFormData((prev) => {
          logger.info("🧪 PREVIOUS FORM DATA:", prev);
          const updatedForm = {
            ...prev,
            personalInfo: {
              ...prev.personalInfo,
              maritalStatus: response.data.marital_status_id?.toString() || "",
            },
            marriageInfo: newMaritalInfo,
          };
          logger.info("🧪 UPDATED FORM DATA:", updatedForm);
          return updatedForm;
        });
      }

      return response.data;
    } catch (error) {
      logger.error("Error testing direct SQL endpoint:", error);
    }
  }, []);

  // Add this after the Marriage Information section in the render
  useEffect(() => {
    // Only try direct SQL approach if client is married
    if (householdId && userId && client?.marital_status_id === 2) {
      const timer = setTimeout(() => {
        testDirectSqlEndpoint(householdId, userId);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [householdId, userId, testDirectSqlEndpoint, client?.marital_status_id]);

  // Derive display labels
  const maritalLabel =
    referenceData.maritalStatusTypes.find(
      (s) =>
        s.marital_status_id.toString() === formData.personalInfo.maritalStatus,
    )?.marital_status_name || "";

  const taxLabel =
    taxFilingStatusOptions.find(
      (t) => t.id?.toString() === formData.taxInfo.filingStatus,
    )?.name || "";

  // Filing state label
  const filingStateLabel =
    filingStateOptions.find(
      (s) => s.id?.toString() === formData.taxInfo.filingState,
    )?.name || "";

  // Primary address
  const { street, unit, city, state, zipCode } = formData.address;
  const primaryAddress =
    `${street}${unit ? `, ${unit}` : ""}, ${city}, ${state} ${zipCode}`.replace(
      /\s+,/g,
      ",",
    );

  // Mailing address (only if different)
  let mailingAddress = "";
  if (
    formData.address.mailingAddress.street ||
    formData.address.mailingAddress.city
  ) {
    const m = formData.address.mailingAddress;
    mailingAddress =
      `${m.street}${m.unit ? `, ${m.unit}` : ""}, ${m.city}, ${m.state} ${m.zipCode}`.replace(
        /\s+,/g,
        ",",
      );
  }

  // Marriage info
  const marriageInfo = {
    marriageDate: formData.marriageInfo.marriageDate,
    cityOfMarriage: formData.marriageInfo.cityOfMarriage,
    stateOfMarriage: formData.marriageInfo.stateOfMarriage,
  };

  // Add pipeline data fetching
  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!householdId || !userId) return;

      try {
        const [stagesResponse, statusResponse] = await Promise.all([
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline/stages`,
          ),
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline`,
          ),
        ]);

        setPipelineData({
          stages: stagesResponse.data || [],
          currentStage: statusResponse.data || null,
        });
      } catch (error) {
        logger.error("Error fetching pipeline data:", error);
      }
    };

    fetchPipelineData();
  }, [householdId, userId]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-semibold">Household Information</h2>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {console.log("Spouse display conditions:", {
          isSpouseSection,
          hasSpouseId: !!client?.spouse_id,
          maritalStatus: client?.marital_status_id,
          spouseInfo: formData.spouseInfo,
        })}
        <MemberCard
          data={formData.personalInfo}
          onFieldChange={handleInputChange}
          errors={errors.personalInfo}
          prefixes={referenceData.prefixes}
          suffixes={referenceData.suffixes}
          genderTypes={referenceData.genderTypes}
          householdId={householdId}
          pipelineData={pipelineData}
        />
        <OverviewCard
          maritalLabel={maritalLabel}
          taxLabel={taxLabel}
          filingStateLabel={filingStateLabel}
          primaryAddress={primaryAddress}
          mailingAddress={mailingAddress}
          marriageInfo={marriageInfo}
          familyCount={familyCount}
          maritalStatusTypes={referenceData.maritalStatusTypes}
          taxFilingStatusOptions={taxFilingStatusOptions}
          filingStateOptions={filingStateOptions}
          onFieldChange={handleInputChange}
          formData={formData}
        />
        {(isSpouseSection || client?.spouse_id) && (
          <MemberCard
            data={formData.spouseInfo}
            onFieldChange={handleInputChange}
            errors={errors.spouseInfo}
            prefixes={referenceData.prefixes}
            suffixes={referenceData.suffixes}
            genderTypes={referenceData.genderTypes}
            householdId={householdId}
            pipelineData={pipelineData}
          />
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          {renderPermissionBadge()}
        </div>

        {hasPermissionError ? (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <strong className="font-bold">Permission Error!</strong>
            <span className="block sm:inline">
              {" "}
              You don't have permission to view or edit this information.
            </span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {getMaritalStatusConfig(formData.personalInfo.maritalStatus)
                .showDivorceDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Divorce Date
                  </label>
                  <input
                    type="date"
                    value={formData.marriageInfo.divorceDate}
                    onChange={(e) =>
                      handleInputChange(
                        "marriageInfo",
                        "divorceDate",
                        "",
                        e.target.value,
                      )
                    }
                    className={inputClass}
                    onFocus={() =>
                      console.log(
                        "🚨 DIVORCE DATE INPUT FOCUS:",
                        formData.marriageInfo.divorceDate,
                      )
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Family Members Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
          Family Members/Trusted Contacts
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData.familyMembers.map((member, i) => (
            <FamilyMemberCard
              key={i}
              index={i}
              member={member}
              errors={errors.familyMembers[i]}
              prefixes={referenceData.prefixes}
              suffixes={referenceData.suffixes}
              genderTypes={referenceData.genderTypes}
              relationshipTypes={relationshipTypes}
              stateOptions={stateOptions}
              onFieldChange={handleFamilyMemberChange}
              onRemove={removeFamilyMember}
              onDependentChange={updateDependentCount}
            />
          ))}
          <FamilyMemberCard isAddCard onClick={addFamilyMember} />
        </div>
      </div>
    </div>
  );
};

export default HouseholdInformation;
